/**
 * Account-level Client Quotes API — sales quotes issued to a Client (see
 * ClientService.ts), independent of any single project workspace. Mirrors
 * ClientService.ts's direct-fetch pattern via the shared `accountRequest`
 * helper in apiClient.ts rather than PlanningApiClient's per-project
 * `planningPath()` — this is account-scoped, not project-scoped.
 */
import { accountRequest } from './apiClient';

export type ClientQuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';
export type ClientQuoteArchiveFilter = 'active' | 'archived' | 'all';

export interface ClientQuoteItem {
  id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface ClientQuote {
  id: string;
  created_by_email: string;
  client_id: string;
  client_name?: string;
  /** The project this quote belongs to. Read this first. */
  project_id?: string | null;
  /** Older servers and older rows only carry this spelling; use it only when project_id is missing. */
  wedding_id?: string | null;
  quote_number: string;
  status: ClientQuoteStatus;
  currency: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  notes: string | null;
  valid_until: string | null;
  converted_invoice_id: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  items?: ClientQuoteItem[];
}

export interface ClientQuoteItemInput {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface ClientQuoteInput {
  client_id: string;
  project_id?: string;
  items: ClientQuoteItemInput[];
  tax_rate?: number;
  valid_until?: string;
  notes?: string;
}

export interface ClientQuoteFilters {
  client_id?: string;
  /** Only this project's quotes. */
  project_id?: string;
  status?: ClientQuoteStatus;
  archived: ClientQuoteArchiveFilter;
}

/** List quotes for the account, optionally filtered by client and status. */
export async function loadClientQuotes(filters: ClientQuoteFilters): Promise<ClientQuote[]> {
  try {
    const params = new URLSearchParams();
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.project_id) params.set('project_id', filters.project_id);
    if (filters.status) params.set('status', filters.status);
    params.set('archived', filters.archived);
    const res = await accountRequest<{ success: true; data: ClientQuote[] }>(
      `/planning/account/client-quotes?${params.toString()}`
    );
    return res.data;
  } catch (error) {
    console.error('Failed to load client quotes:', error);
    throw error instanceof Error ? error : new Error('Failed to load client quotes.');
  }
}

export async function getClientQuoteById(id: string): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>(`/planning/account/client-quotes/${id}`);
  return res.data;
}

export async function createClientQuote(input: ClientQuoteInput): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>('/planning/account/client-quotes', {
    method: 'POST',
    body: input,
  });
  return res.data;
}

/** Only valid while the quote is still a draft — the server 409s otherwise. */
export async function updateClientQuote(id: string, input: ClientQuoteInput): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>(`/planning/account/client-quotes/${id}`, {
    method: 'PUT',
    body: input,
  });
  return res.data;
}

export async function sendClientQuote(id: string): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>(`/planning/account/client-quotes/${id}/send`, {
    method: 'POST',
  });
  return res.data;
}

/** Accepting a quote also creates the resulting invoice server-side. */
export async function acceptClientQuote(id: string): Promise<{ quote: ClientQuote; invoice: { id: string; invoice_number: string } }> {
  const res = await accountRequest<{ success: true; data: { quote: ClientQuote; invoice: { id: string; invoice_number: string } } }>(
    `/planning/account/client-quotes/${id}/accept`,
    { method: 'POST' }
  );
  return res.data;
}

export async function declineClientQuote(id: string): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>(`/planning/account/client-quotes/${id}/decline`, {
    method: 'POST',
  });
  return res.data;
}

/** Un-archives a previously archived quote. */
export async function restoreClientQuote(id: string): Promise<ClientQuote> {
  const res = await accountRequest<{ success: true; data: ClientQuote }>(`/planning/account/client-quotes/${id}/restore`, {
    method: 'POST',
  });
  return res.data;
}

/** Archives the quote (default) or permanently deletes it (only valid once already archived). */
export async function deleteClientQuote(id: string, permanent = false): Promise<void> {
  await accountRequest<{ success: true }>(`/planning/account/client-quotes/${id}${permanent ? '?permanent=true' : ''}`, {
    method: 'DELETE',
  });
}
